import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Briefcase, Plus, MapPin, Users, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function AdminRecruitment() {
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [form, setForm] = useState({ title: '', department: '', location: 'Remote', type: 'full_time', description: '', salary_range: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [jRes, aRes] = await Promise.all([
        api.get('/api/recruitment/jobs?status=open'),
        api.get('/api/recruitment/applications').catch(() => ({ data: { applications: [] } }))
      ]);
      setJobs(jRes.data.jobs || []);
      setApps(aRes.data.applications || []);
    } catch {} finally { setLoading(false); }
  };

  const createJob = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    try { await api.post('/api/recruitment/jobs', form); toast.success('Job posted'); setShowCreate(false); loadData(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-recruitment-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold flex items-center gap-2"><Briefcase className="w-6 h-6 text-[#B89B5E]" /> Recruitment</h1><p className="text-sm text-[#5B616E] mt-1">Manage job postings and applications</p></div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Plus className="w-4 h-4 mr-2" /> Post Job</Button>
      </div>

      {loading ? <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-40 rounded-xl" />)}</div> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {jobs.map(j => (
              <Card key={j.id} className="bg-white border-[#E7E0D6] cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedJob(j)}>
                <CardContent className="pt-5">
                  <Badge variant="outline" className="text-[10px] capitalize">{j.type?.replace('_', ' ')}</Badge>
                  <h3 className="font-medium mt-2">{j.title}</h3>
                  <div className="flex items-center gap-4 mt-2 text-xs text-[#5B616E]">
                    {j.department && <span>{j.department}</span>}
                    {j.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{j.location}</span>}
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{j.applications_count || 0} applicants</span>
                  </div>
                  {j.salary_range && <p className="text-xs text-[#B89B5E] mt-2">{j.salary_range}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
          {jobs.length === 0 && <div className="text-center py-16 text-[#5B616E]"><Briefcase className="w-16 h-16 mx-auto mb-4 opacity-30" /><p>No open positions</p></div>}

          {apps.length > 0 && (
            <Card className="bg-white border-[#E7E0D6]">
              <CardHeader><CardTitle className="text-sm">Recent Applications ({apps.length})</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">{apps.slice(0, 10).map(a => (
                  <div key={a.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#FBF7EF] text-sm">
                    <div className="flex-1"><span className="font-medium">{a.name}</span><span className="text-[#5B616E]"> &middot; {a.email}</span></div>
                    <Badge variant="outline" className="text-[10px]">{a.status}</Badge>
                  </div>
                ))}</div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Post New Job</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Job Title" />
            <div className="grid grid-cols-2 gap-4">
              <Input value={form.department} onChange={e => setForm({...form, department: e.target.value})} placeholder="Department" />
              <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="Location" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Select value={form.type} onValueChange={v => setForm({...form, type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="full_time">Full Time</SelectItem><SelectItem value="part_time">Part Time</SelectItem><SelectItem value="contract">Contract</SelectItem><SelectItem value="freelance">Freelance</SelectItem></SelectContent></Select>
              <Input value={form.salary_range} onChange={e => setForm({...form, salary_range: e.target.value})} placeholder="Salary Range" />
            </div>
            <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Job Description" rows={4} />
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={createJob} className="bg-[#121316] text-white">Post Job</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
